import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { SupportAiSuggestButton } from '../../components/customerSuccess/SupportAiSuggestButton';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { listActiveContractsByCompany, Contract } from '../../services/commercialContractsApi';
import { listCompanies, Company } from '../../services/coreCompaniesApi';
import {
  listTicketTeamMembers,
  listTicketTeams,
  listServiceTickets,
  ServiceTicketTeam,
  ServiceTicketTeamMember,
} from '../../services/customerSuccessTicketsApi';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import {
  ServiceProblem,
  ServiceProblemTicketSummary,
  ServiceProblemUpsertPayload,
  broadcastProblemReply,
  createServiceProblem,
  deleteServiceProblem,
  linkTicketToProblem,
  listProblemLinkedTickets,
  listServiceProblems,
  suggestProblemBroadcastMessage,
  unlinkTicketFromProblem,
  updateServiceProblem,
} from '../../services/serviceProblemsApi';

const STATUS_LABELS: Record<string, string> = {
  Investigating: 'Investigando',
  KnownError: 'Erro conhecido',
  Resolved: 'Resolvido',
};

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Baixa',
  Medium: 'Média',
  High: 'Alta',
  Critical: 'Crítica',
};

const emptyForm = (): ServiceProblemUpsertPayload => ({
  title: '',
  description: '',
  rootCause: '',
  workaround: '',
  status: 'Investigating',
  priority: 'Medium',
});

export function ProblemsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('customer-success.problems');

  const [items, setItems] = useState<ServiceProblem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ServiceProblemUpsertPayload>(emptyForm());
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [companyContracts, setCompanyContracts] = useState<Contract[]>([]);
  const [teams, setTeams] = useState<ServiceTicketTeam[]>([]);
  const [teamMembers, setTeamMembers] = useState<ServiceTicketTeamMember[]>([]);
  const [linkedTickets, setLinkedTickets] = useState<ServiceProblemTicketSummary[]>([]);
  const [linkTicketId, setLinkTicketId] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [suggestingBroadcast, setSuggestingBroadcast] = useState(false);
  const [useWorkaround, setUseWorkaround] = useState(false);
  const [onlyOpenTickets, setOnlyOpenTickets] = useState(true);
  const [selectedBroadcastTicketIds, setSelectedBroadcastTicketIds] = useState<string[]>([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [availableTickets, setAvailableTickets] = useState<
    { id: string; ticketCode: string; title: string; companyId: string }[]
  >([]);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadLookups(): Promise<void> {
    try {
      const [companyResult, productResult, teamList, members, board] = await Promise.all([
        listCompanies({ activeOnly: true, pageSize: 200 }),
        listSalesProducts({ pageSize: 200 }),
        listTicketTeams(),
        listTicketTeamMembers(),
        listServiceTickets({ page: 1, pageSize: 200 }),
      ]);
      setCompanies(companyResult.items);
      setProducts(productResult.items);
      setTeams(teamList);
      setTeamMembers(members);
      setAvailableTickets(
        board.items.map((ticket) => ({
          id: ticket.id,
          ticketCode: ticket.ticketCode,
          title: ticket.title,
          companyId: ticket.companyId,
        })),
      );
    } catch {
      toast.error('Falha ao carregar dados auxiliares.');
    }
  }

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listServiceProblems({
        page: targetPage,
        pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar problemas.');
    } finally {
      setLoading(false);
    }
  }

  async function loadLinkedTickets(problemId: string): Promise<void> {
    try {
      const tickets = await listProblemLinkedTickets(problemId);
      setLinkedTickets(tickets);
      setSelectedBroadcastTicketIds(tickets.map((ticket) => ticket.id));
    } catch {
      setLinkedTickets([]);
      setSelectedBroadcastTicketIds([]);
    }
  }

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  useEffect(() => {
    if (!form.companyId) {
      setCompanyContracts([]);
      return;
    }
    void (async () => {
      try {
        setCompanyContracts(await listActiveContractsByCompany(form.companyId!));
      } catch {
        setCompanyContracts([]);
      }
    })();
  }, [form.companyId]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm());
    setLinkedTickets([]);
    setLinkTicketId('');
    setBroadcastBody('');
    setUseWorkaround(false);
    setOnlyOpenTickets(true);
    setSelectedBroadcastTicketIds([]);
  }

  function startEdit(problem: ServiceProblem): void {
    expandForm();
    setEditingId(problem.id);
    setForm({
      title: problem.title,
      description: problem.description ?? '',
      rootCause: problem.rootCause ?? '',
      workaround: problem.workaround ?? '',
      status: problem.status,
      priority: problem.priority,
      companyId: problem.companyId ?? undefined,
      salesProductId: problem.salesProductId ?? undefined,
      contractId: problem.contractId ?? undefined,
      assignedTeamId: problem.assignedTeamId ?? undefined,
      assignedToUserId: problem.assignedToUserId ?? undefined,
    });
    setLinkTicketId('');
    setBroadcastBody('');
    setUseWorkaround(Boolean(problem.workaround));
    setOnlyOpenTickets(true);
    void loadLinkedTickets(problem.id);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      if (editingId) {
        await updateServiceProblem(editingId, form);
        toast.success('Problema atualizado.');
        resetForm();
      } else {
        await createServiceProblem(form);
        toast.success('Problema criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar problema.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(problem: ServiceProblem): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Excluir problema',
        message: `Excluir "${problem.title}"? Tickets vinculados serão desvinculados.`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteServiceProblem(problem.id);
      toast.success('Problema excluído.');
      if (editingId === problem.id) collapseForm(resetForm);
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir problema.');
    }
  }

  async function handleLinkTicket(): Promise<void> {
    if (!editingId || !linkTicketId) return;
    try {
      await linkTicketToProblem(editingId, linkTicketId);
      toast.success('Ticket vinculado.');
      setLinkTicketId('');
      await loadLinkedTickets(editingId);
      await loadData(page);
    } catch {
      toast.error('Falha ao vincular ticket.');
    }
  }

  async function handleUnlinkTicket(ticketId: string): Promise<void> {
    if (!editingId) return;
    try {
      await unlinkTicketFromProblem(editingId, ticketId);
      toast.success('Ticket desvinculado.');
      await loadLinkedTickets(editingId);
      await loadData(page);
    } catch {
      toast.error('Falha ao desvincular ticket.');
    }
  }

  function toggleBroadcastTicket(ticketId: string): void {
    setSelectedBroadcastTicketIds((current) =>
      current.includes(ticketId)
        ? current.filter((id) => id !== ticketId)
        : [...current, ticketId],
    );
  }

  async function handleSuggestBroadcast(): Promise<void> {
    if (!editingId || !canWrite || suggestingBroadcast) return;
    setSuggestingBroadcast(true);
    try {
      const hintParts = [
        broadcastBody.trim(),
        useWorkaround && form.workaround?.trim() ? `Workaround: ${form.workaround.trim()}` : '',
      ]
        .filter(Boolean)
        .join('\n\n');
      const result = await suggestProblemBroadcastMessage(editingId, hintParts || undefined);
      setBroadcastBody(result.text);
      toast.success('Sugestão de mensagem gerada. Revise antes de enviar.');
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detail ?? 'Não foi possível sugerir a mensagem com IA.');
    } finally {
      setSuggestingBroadcast(false);
    }
  }

  async function handleBroadcastReply(): Promise<void> {
    if (!editingId || !canWrite) return;

    const hasBody = broadcastBody.trim().length > 0;
    const hasWorkaround = useWorkaround && Boolean(form.workaround?.trim());
    if (!hasBody && !hasWorkaround) {
      toast.error('Informe a mensagem ou marque “Usar workaround”.');
      return;
    }

    if (selectedBroadcastTicketIds.length === 0) {
      toast.error('Selecione ao menos um ticket.');
      return;
    }

    if (
      !(await confirm({
        title: 'Responder a todos',
        message: `Enviar a mesma resposta para ${selectedBroadcastTicketIds.length} ticket(s) vinculado(s)? A mensagem será pública e enviada pelo canal de cada ticket quando disponível.`,
        confirmLabel: 'Enviar',
      }))
    ) {
      return;
    }

    setBroadcasting(true);
    try {
      const result = await broadcastProblemReply(editingId, {
        body: hasBody ? broadcastBody.trim() : undefined,
        useWorkaround,
        onlyOpenTickets,
        ticketIds: selectedBroadcastTicketIds,
      });

      if (result.failedCount === 0) {
        toast.success(
          `Resposta enviada a ${result.sentCount} ticket(s)${
            result.skippedCount > 0 ? ` (${result.skippedCount} ignorado(s))` : ''
          }.`,
        );
      } else {
        toast.warning(
          `Enviados: ${result.sentCount}. Falhas: ${result.failedCount}. Ignorados: ${result.skippedCount}.`,
        );
      }

      setBroadcastBody('');
    } catch (error: unknown) {
      const detail =
        typeof error === 'object'
        && error !== null
        && 'response' in error
        && typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
          'string'
          ? (error as { response: { data: { detail: string } } }).response.data.detail
          : 'Falha ao enviar resposta em massa.';
      toast.error(detail);
    } finally {
      setBroadcasting(false);
    }
  }

  const linkableTickets = availableTickets.filter((ticket) => {
    if (linkedTickets.some((linked) => linked.id === ticket.id)) return false;
    if (form.companyId && ticket.companyId !== form.companyId) return false;
    return true;
  });

  return (
    <PageShell>
      <PageHeader
        title="Gestão de problemas"
        description="Agrupe tickets do mesmo incidente em um problema, registre causa/workaround e responda a todos de uma vez."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar problema' : 'Novo problema'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.title || 'problema selecionado'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar um novo problema"
          isEditing={!!editingId}
        >
          <form className="px-4 pb-4 pt-3" onSubmit={(event) => void handleSubmit(event)}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <label className="block">
              <span className="erp-label">Título <RequiredMark /></span>
              <input
                className="erp-input mt-1 w-full"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="erp-label">Status</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.status ?? 'Investigating'}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Prioridade</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.priority ?? 'Medium'}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Empresa</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.companyId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    companyId: e.target.value || undefined,
                    contractId: undefined,
                  })
                }
              >
                <option value="">Todas / não definida</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Produto</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.salesProductId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, salesProductId: e.target.value || undefined })
                }
              >
                <option value="">—</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Contrato</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.contractId ?? ''}
                onChange={(e) => setForm({ ...form, contractId: e.target.value || undefined })}
                disabled={!form.companyId}
              >
                <option value="">—</option>
                {companyContracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contractCode} — {contract.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Equipe</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.assignedTeamId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, assignedTeamId: e.target.value || undefined })
                }
              >
                <option value="">—</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Responsável</span>
              <select
                className="erp-input mt-1 w-full"
                value={form.assignedToUserId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, assignedToUserId: e.target.value || undefined })
                }
              >
                <option value="">—</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block md:col-span-2 lg:col-span-3">
              <span className="erp-label">Descrição</span>
              <textarea
                className="erp-input mt-1 w-full"
                rows={2}
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <label className="block md:col-span-2 lg:col-span-3">
              <span className="erp-label">Causa raiz</span>
              <textarea
                className="erp-input mt-1 w-full"
                rows={2}
                value={form.rootCause ?? ''}
                onChange={(e) => setForm({ ...form, rootCause: e.target.value })}
              />
            </label>
            <label className="block md:col-span-2 lg:col-span-3">
              <span className="erp-label">Workaround</span>
              <textarea
                className="erp-input mt-1 w-full"
                rows={2}
                value={form.workaround ?? ''}
                onChange={(e) => setForm({ ...form, workaround: e.target.value })}
              />
            </label>

            {editingId ? (
              <div className="md:col-span-2 lg:col-span-3 rounded-lg border border-secondary-200 p-4">
                <h4 className="mb-3 font-medium text-secondary-800">Tickets vinculados</h4>
                {linkedTickets.length === 0 ? (
                  <p className="mb-3 text-sm text-secondary-500">Nenhum ticket vinculado.</p>
                ) : (
                  <ul className="mb-3 space-y-2">
                    {linkedTickets.map((ticket) => (
                      <li
                        key={ticket.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded border border-secondary-100 px-3 py-2 text-sm"
                      >
                        <label className="flex min-w-0 flex-1 items-start gap-2">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedBroadcastTicketIds.includes(ticket.id)}
                            onChange={() => toggleBroadcastTicket(ticket.id)}
                          />
                          <span className="min-w-0">
                            <Link
                              to={`/customer-success/tickets?selected=${ticket.id}`}
                              className="font-medium text-primary-600 hover:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {ticket.ticketCode}
                            </Link>
                            <span className="text-secondary-600"> — {ticket.title}</span>
                            <span className="ml-2 text-xs text-secondary-500">
                              {STATUS_LABELS[ticket.status] ?? ticket.status}
                            </span>
                          </span>
                        </label>
                        <button
                          type="button"
                          className="erp-btn-secondary text-xs"
                          onClick={() => void handleUnlinkTicket(ticket.id)}
                        >
                          Desvincular
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <label className="block min-w-[240px] flex-1">
                    <span className="erp-label">Vincular ticket</span>
                    <select
                      className="erp-input mt-1 w-full"
                      value={linkTicketId}
                      onChange={(e) => setLinkTicketId(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {linkableTickets.map((ticket) => (
                        <option key={ticket.id} value={ticket.id}>
                          {ticket.ticketCode} — {ticket.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="erp-btn-secondary"
                    disabled={!linkTicketId}
                    onClick={() => void handleLinkTicket()}
                  >
                    Vincular
                  </button>
                </div>

                {linkedTickets.length > 0 ? (
                  <div className="mt-4 border-t border-secondary-100 pt-4">
                    <h4 className="mb-2 font-medium text-secondary-800">
                      Responder a todos (agrupar mensagens)
                    </h4>
                    <p className="mb-3 text-sm text-secondary-500">
                      Envia a mesma resposta pública aos tickets selecionados e notifica o cliente
                      pelo canal de cada um, quando houver.
                    </p>
                    <label className="mb-3 block">
                      <span className="mb-1 flex items-center justify-between gap-2">
                        <span className="erp-label mb-0">Mensagem</span>
                        <SupportAiSuggestButton
                          suggesting={suggestingBroadcast}
                          disabled={broadcasting}
                          onClick={() => void handleSuggestBroadcast()}
                          title="Sugerir resposta em massa com IA"
                        />
                      </span>
                      <textarea
                        className="erp-input mt-1 w-full"
                        rows={3}
                        value={broadcastBody}
                        onChange={(e) => setBroadcastBody(e.target.value)}
                        placeholder="Texto da resposta compartilhada..."
                        disabled={broadcasting || suggestingBroadcast}
                      />
                    </label>
                    <div className="mb-3 flex flex-wrap gap-4 text-sm text-secondary-700">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={useWorkaround}
                          onChange={(e) => setUseWorkaround(e.target.checked)}
                          disabled={broadcasting || suggestingBroadcast}
                        />
                        Usar workaround se a mensagem estiver vazia
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={onlyOpenTickets}
                          onChange={(e) => setOnlyOpenTickets(e.target.checked)}
                          disabled={broadcasting || suggestingBroadcast}
                        />
                        Somente tickets abertos
                      </label>
                    </div>
                    <button
                      type="button"
                      className="erp-btn-primary"
                      disabled={
                        broadcasting || suggestingBroadcast || selectedBroadcastTicketIds.length === 0
                      }
                      onClick={() => void handleBroadcastReply()}
                    >
                      {broadcasting
                        ? 'Enviando...'
                        : `Enviar a ${selectedBroadcastTicketIds.length} ticket(s)`}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions md:col-span-2 lg:col-span-3"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </div>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="erp-label">Buscar</span>
          <input
            className="erp-input mt-1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Título, descrição ou causa raiz"
          />
        </label>
        <label className="block">
          <span className="erp-label">Status</span>
          <select
            className="erp-input mt-1"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Filtrar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="erp-card overflow-x-auto">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Título</th>
                  <th>Status</th>
                  <th>Prioridade</th>
                  <th>Empresa</th>
                  <th>Tickets</th>
                  <th>Atualizado</th>
                  {canWrite ? <th className="w-24" /> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 8 : 7} className="text-center text-secondary-500">
                      Nenhum problema encontrado.
                    </td>
                  </tr>
                ) : (
                  items.map((problem) => (
                    <tr key={problem.id}>
                      <td className="font-mono text-sm">{problem.problemCode}</td>
                      <td>{problem.title}</td>
                      <td>
                        <CodedStatusBadge
                          status={problem.status}
                          label={STATUS_LABELS[problem.status] ?? problem.status}
                        />
                      </td>
                      <td>{PRIORITY_LABELS[problem.priority] ?? problem.priority}</td>
                      <td>{problem.companyName ?? '—'}</td>
                      <td>{problem.linkedTicketCount}</td>
                      <td>{new Date(problem.updatedAtUtc).toLocaleString('pt-BR')}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(problem)} />
                            <DeleteIconButton onClick={() => void handleDelete(problem)} />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          />
        </>
      )}
    </PageShell>
  );
}
